
import { showToast } from '/src/v4/toast.js';

const STORAGE_KEY = 'gentelella:theme-overrides';

const state = {
  primary:    '#1ABB9C',
  primaryDk:  '#169f85',
  sidebarBg:  '#1a2332',
  radius:     6,
  sidebarW:   252,
  fontSize:   14,
  mode:       document.documentElement.getAttribute('data-theme') || 'light'
};

// Saved overrides (persist across reloads on this page only via the
// window-scope inline rule below, not globally).
function load() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null') || {}; } catch (_e) { return {}; }
}
function save() {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_e) {}
}
Object.assign(state, load());

// Build a <style> tag with our overrides — no global storage so the rest
// of the template stays on the user's actual --primary outside this page.
const styleTag = document.createElement('style');
styleTag.id = 'theme-overrides';
document.head.appendChild(styleTag);

function applyOverrides() {
  // Keep the primary translucent variant (--primary-lt) in sync with the
  // chosen color so backgrounds for active states still look right.
  const lt = hexToRgba(state.primary, 0.10);
  styleTag.textContent = `
    :root {
      --primary: ${state.primary};
      --primary-dk: ${state.primaryDk};
      --primary-lt: ${lt};
      --radius: ${state.radius}px;
      --radius-sm: ${Math.max(0, state.radius - 2)}px;
      --radius-lg: ${state.radius + 2}px;
      --sidebar-w: ${state.sidebarW}px;
      --font-size: ${state.fontSize / 16}rem;
      ${state.sidebarBg === 'primary'
        ? `--sidebar-bg: ${state.primary}; --sidebar-active: rgba(255,255,255,0.18); --sidebar-text: rgba(255,255,255,0.7); --sidebar-text-active: #fff; --sidebar-text-hover: #fff; --sidebar-hover: rgba(255,255,255,0.10); --sidebar-border: rgba(255,255,255,0.18);`
        : state.sidebarBg === '#ffffff'
          ? `--sidebar-bg: #ffffff; --sidebar-text: var(--text-secondary); --sidebar-text-active: var(--primary); --sidebar-text-hover: var(--text); --sidebar-hover: var(--bg-surface-secondary); --sidebar-active: var(--primary-lt); --sidebar-border: var(--border-color-light);`
          : `--sidebar-bg: ${state.sidebarBg};`
      }
    }
  `;
  document.documentElement.setAttribute('data-theme', state.mode);
  paintTokenOutput();
  save();
  // Re-init charts so they pick up the new tokens (mutation observer in
  // charts.js handles data-theme changes; manually trigger by toggling).
  document.documentElement.dispatchEvent(new CustomEvent('themechange'));
}

function hexToRgba(hex, alpha) {
  const m = hex.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return hex;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function paintTokenOutput() {
  const out = `// Generated by Gentelella v4 theme generator
// Paste into src/scss/v4/_tokens.scss
:root {
  --primary:    ${state.primary};
  --primary-dk: ${state.primaryDk};
  --primary-lt: ${hexToRgba(state.primary, 0.10)};

  --radius:    ${state.radius}px;
  --radius-sm: ${Math.max(0, state.radius - 2)}px;
  --radius-lg: ${state.radius + 2}px;

  --sidebar-bg: ${state.sidebarBg === 'primary' ? state.primary : state.sidebarBg};
  --sidebar-w:  ${state.sidebarW}px;

  --font-size:  ${state.fontSize / 16}rem;
}`;
  document.getElementById('tokens-output').textContent = out;
}

// ── Bind controls ──

const swatches = document.querySelectorAll('.theme-swatch');
swatches.forEach((sw) => {
  if (sw.dataset.primary === state.primary) {sw.classList.add('active');} else {sw.classList.remove('active');}
  sw.addEventListener('click', () => {
    swatches.forEach((s) => s.classList.remove('active'));
    sw.classList.add('active');
    state.primary = sw.dataset.primary;
    state.primaryDk = sw.dataset.primaryDk;
    document.getElementById('custom-color').value = state.primary;
    document.getElementById('custom-color-picker').value = state.primary;
    applyOverrides();
  });
});

const customInput = document.getElementById('custom-color');
const customPicker = document.getElementById('custom-color-picker');
const updateCustom = (val) => {
  if (!/^#[0-9a-f]{6}$/i.test(val)) {return;}
  state.primary = val;
  state.primaryDk = darken(val, 0.12);
  swatches.forEach((s) => s.classList.toggle('active', s.dataset.primary?.toLowerCase() === val.toLowerCase()));
  applyOverrides();
};
customInput.addEventListener('change', (e) => updateCustom(e.target.value));
customPicker.addEventListener('input', (e) => { customInput.value = e.target.value; updateCustom(e.target.value); });

function darken(hex, amount) {
  const m = hex.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return hex;
  const r = Math.max(0, Math.round(parseInt(m[1], 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(m[2], 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(m[3], 16) * (1 - amount)));
  return '#' + [r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('');
}

document.querySelectorAll('input[name="sidebar-bg"]').forEach((r) => {
  if (r.value === state.sidebarBg) {r.checked = true;}
  r.addEventListener('change', () => { if (r.checked) { state.sidebarBg = r.value; applyOverrides(); } });
});

const radiusSlider = document.getElementById('radius-slider');
const radiusDisplay = document.getElementById('radius-display');
radiusSlider.value = state.radius;
radiusDisplay.textContent = `${state.radius} px`;
radiusSlider.addEventListener('input', () => {
  state.radius = parseInt(radiusSlider.value, 10);
  radiusDisplay.textContent = `${state.radius} px`;
  applyOverrides();
});

const sbSlider = document.getElementById('sidebar-w-slider');
const sbDisplay = document.getElementById('sidebar-w-display');
sbSlider.value = state.sidebarW;
sbDisplay.textContent = `${state.sidebarW} px`;
sbSlider.addEventListener('input', () => {
  state.sidebarW = parseInt(sbSlider.value, 10);
  sbDisplay.textContent = `${state.sidebarW} px`;
  applyOverrides();
});

const fsSlider = document.getElementById('font-size-slider');
const fsDisplay = document.getElementById('font-size-display');
fsSlider.value = state.fontSize;
fsDisplay.textContent = `${state.fontSize} px`;
fsSlider.addEventListener('input', () => {
  state.fontSize = parseFloat(fsSlider.value);
  fsDisplay.textContent = `${state.fontSize} px`;
  applyOverrides();
});

document.querySelectorAll('input[name="mode"]').forEach((r) => {
  if (r.value === state.mode) {r.checked = true;}
  r.addEventListener('change', () => { if (r.checked) { state.mode = r.value; applyOverrides(); } });
});

// Reset / copy / download
document.getElementById('reset-theme').addEventListener('click', () => {
  Object.assign(state, { primary: '#1ABB9C', primaryDk: '#169f85', sidebarBg: '#1a2332', radius: 6, sidebarW: 252, fontSize: 14, mode: 'light' });
  swatches.forEach((s, i) => s.classList.toggle('active', i === 0));
  customInput.value = state.primary;
  customPicker.value = state.primary;
  document.querySelectorAll('input[name="sidebar-bg"]').forEach((r) => { r.checked = r.value === state.sidebarBg; });
  document.querySelectorAll('input[name="mode"]').forEach((r) => { r.checked = r.value === state.mode; });
  radiusSlider.value = state.radius; radiusDisplay.textContent = `${state.radius} px`;
  sbSlider.value = state.sidebarW; sbDisplay.textContent = `${state.sidebarW} px`;
  fsSlider.value = state.fontSize; fsDisplay.textContent = `${state.fontSize} px`;
  applyOverrides();
  showToast('Theme reset');
});

const copy = async () => {
  try { await navigator.clipboard.writeText(document.getElementById('tokens-output').textContent); showToast('SCSS copied', { variant: 'success' }); }
  catch (_e) { showToast('Copy failed — select and Ctrl+C', { variant: 'error' }); }
};
document.getElementById('copy-tokens').addEventListener('click', copy);
document.getElementById('copy-tokens-2').addEventListener('click', copy);

document.getElementById('download-tokens').addEventListener('click', () => {
  const content = document.getElementById('tokens-output').textContent;
  const blob = new Blob([content], { type: 'text/x-scss' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = '_tokens-override.scss';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  showToast('Downloaded _tokens-override.scss', { variant: 'success' });
});

// Initial render
applyOverrides();
